import { type FormEvent, useState } from 'react';
import type { FaixaCapacidade, FaixaCapacidadeNova, RetornoLinha } from '@pcp/contracts';
import { useCapacidade, useGravarLote, useMaquinas, isOk } from '../../api/cadastros';
import { type Column, EditableTable } from '../../components/EditableTable';
import { ErrorState, LoadingBlock } from '../../components/states';
import { t } from '../../i18n/pt-BR';
import { num } from './shared';

const c = t.cadastros.capacity;

const key = (f: FaixaCapacidade) =>
  [f.maquina, f['fm-codigo'], f.subfamilia, f['espessura-ini'], f['largura-ini'], f['peso-esp-ini']].join(
    '|',
  );

const EMPTY: FaixaCapacidadeNova = {
  maquina: '',
  'fm-codigo': '*',
  subfamilia: '*',
  'espessura-ini': 0,
  'espessura-fim': 0.4,
  'largura-ini': 0,
  'largura-fim': 1000,
  'peso-esp-ini': 0,
  'peso-esp-fim': 9.99,
  velocidade: 0,
  'taxa-kgh': 0,
  'setup-min': 0,
  'pct-perda': 0,
  prioridade: 100,
  ativo: true,
};

export function CapacidadeTab({ canEdit }: { canEdit: boolean }) {
  const [machine, setMachine] = useState('');
  const machines = useMaquinas();
  const query = useCapacidade(machine ? { maquina: machine } : {});
  const lote = useGravarLote('capacidade');
  const [draft, setDraft] = useState<FaixaCapacidadeNova>(EMPTY);
  const [createResult, setCreateResult] = useState<RetornoLinha | null>(null);

  const any = (v: string) => (v === '*' ? c.any : v);
  const range = (a: number, b: number) => `${num(a)} – ${num(b)}`;

  const columns: Column<FaixaCapacidade>[] = [
    { key: 'maquina', header: c.machine, read: (r) => <strong>{r.maquina}</strong> },
    { key: 'fm-codigo', header: c.family, read: (r) => any(r['fm-codigo']) },
    { key: 'subfamilia', header: c.subfamily, read: (r) => any(r.subfamilia) },
    {
      key: 'espessura',
      header: c.thickness,
      numeric: true,
      read: (r) => range(r['espessura-ini'], r['espessura-fim']),
    },
    {
      key: 'largura',
      header: c.width,
      numeric: true,
      read: (r) => range(r['largura-ini'], r['largura-fim']),
    },
    {
      key: 'velocidade',
      header: c.speed,
      numeric: true,
      read: (r) => num(r.velocidade),
      edit: { kind: 'number', field: 'velocidade', step: 1, min: 0 },
    },
    {
      key: 'taxa-kgh',
      header: c.fixedRate,
      numeric: true,
      read: (r) => num(r['taxa-kgh']),
      edit: { kind: 'number', field: 'taxa-kgh', step: 1, min: 0 },
    },
    {
      key: 'theoretical',
      header: c.theoretical,
      numeric: true,
      read: (r) => (r['theoretical-kg-h-sample'] === undefined ? '–' : num(r['theoretical-kg-h-sample'], 0)),
    },
    {
      key: 'setup-min',
      header: c.setup,
      numeric: true,
      read: (r) => num(r['setup-min']),
      edit: { kind: 'number', field: 'setup-min', step: 1, min: 0 },
    },
    {
      key: 'pct-perda',
      header: c.loss,
      numeric: true,
      read: (r) => num(r['pct-perda']),
      edit: { kind: 'number', field: 'pct-perda', step: 0.1, min: 0, max: 100 },
    },
    {
      key: 'prioridade',
      header: c.priority,
      numeric: true,
      read: (r) => r.prioridade,
      edit: { kind: 'number', field: 'prioridade', step: 1 },
    },
    {
      key: 'ativo',
      header: c.active,
      read: (r) => (r.ativo ? t.cadastros.yes : t.cadastros.no),
      edit: { kind: 'check', field: 'ativo' },
    },
  ];

  const save = async (row: FaixaCapacidade, changes: Partial<FaixaCapacidade>) => {
    const [r] = await lote.mutateAsync({ metodo: 'PUT', linhas: [{ ...row, ...changes }] });
    return r!;
  };
  const remove = async (row: FaixaCapacidade) => {
    const [r] = await lote.mutateAsync({ metodo: 'DELETE', linhas: [row] });
    return r!;
  };

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    const [r] = await lote.mutateAsync({ metodo: 'POST', linhas: [draft] });
    setCreateResult(r!);
    if (r && isOk(r)) setDraft(EMPTY);
  };

  const setNum = (field: keyof FaixaCapacidadeNova) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setDraft((d) => ({ ...d, [field]: Number(e.target.value) }));

  return (
    <section className="panel" aria-labelledby="tab-capacity">
      <h2 id="tab-capacity">{t.cadastros.tabs.capacity}</h2>
      <p className="hint">{c.hint}</p>
      <div className="row">
        <label className="inline-label" htmlFor="cap-machine">
          {c.machine}
        </label>
        <select
          id="cap-machine"
          className="select"
          value={machine}
          onChange={(e) => setMachine(e.target.value)}
        >
          <option value="">{t.cadastros.all}</option>
          {(machines.data ?? []).map((m) => (
            <option key={m.maquina} value={m.maquina}>
              {m.descricao}
            </option>
          ))}
        </select>
      </div>
      {query.isLoading ? <LoadingBlock /> : null}
      {query.error ? <ErrorState error={query.error} onRetry={() => void query.refetch()} /> : null}
      {query.data ? (
        <EditableTable
          rows={query.data}
          rowKey={key}
          rowLabel={(r) => `${r.maquina} ${r['espessura-ini']}–${r['espessura-fim']}`}
          columns={columns}
          canEdit={canEdit}
          onSave={save}
          onDelete={remove}
          onReload={() => void query.refetch()}
        />
      ) : null}

      {canEdit ? (
        <form onSubmit={(e) => void onCreate(e)} className="new-row" aria-label={c.newTitle}>
          <strong>{c.newTitle}</strong>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="nc-maquina">{c.machine}</label>
              <select
                id="nc-maquina"
                className="select"
                value={draft.maquina}
                onChange={(e) => setDraft((d) => ({ ...d, maquina: e.target.value }))}
              >
                <option value="" />
                {(machines.data ?? []).map((m) => (
                  <option key={m.maquina} value={m.maquina}>
                    {m.descricao}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="nc-fam">{c.family}</label>
              <input
                id="nc-fam"
                value={draft['fm-codigo']}
                onChange={(e) => setDraft((d) => ({ ...d, 'fm-codigo': e.target.value }))}
              />
            </div>
            <div className="field">
              <label htmlFor="nc-sub">{c.subfamily}</label>
              <input
                id="nc-sub"
                value={draft.subfamilia}
                onChange={(e) => setDraft((d) => ({ ...d, subfamilia: e.target.value }))}
              />
            </div>
            <div className="field">
              <label htmlFor="nc-ei">{c.thickness} (de)</label>
              <input
                id="nc-ei"
                type="number"
                step="any"
                value={draft['espessura-ini']}
                onChange={setNum('espessura-ini')}
              />
            </div>
            <div className="field">
              <label htmlFor="nc-ef">{c.thickness} (até)</label>
              <input
                id="nc-ef"
                type="number"
                step="any"
                value={draft['espessura-fim']}
                onChange={setNum('espessura-fim')}
              />
            </div>
            <div className="field">
              <label htmlFor="nc-li">{c.width} (de)</label>
              <input
                id="nc-li"
                type="number"
                step="any"
                value={draft['largura-ini']}
                onChange={setNum('largura-ini')}
              />
            </div>
            <div className="field">
              <label htmlFor="nc-lf">{c.width} (até)</label>
              <input
                id="nc-lf"
                type="number"
                step="any"
                value={draft['largura-fim']}
                onChange={setNum('largura-fim')}
              />
            </div>
            <div className="field">
              <label htmlFor="nc-vel">{c.speed}</label>
              <input
                id="nc-vel"
                type="number"
                step="any"
                value={draft.velocidade}
                onChange={setNum('velocidade')}
              />
            </div>
            <div className="field" style={{ alignSelf: 'end' }}>
              <button
                type="submit"
                className="btn"
                disabled={!draft.maquina || draft.velocidade <= 0 || lote.isPending}
              >
                {t.cadastros.add}
              </button>
            </div>
          </div>
          {createResult && !isOk(createResult) ? (
            <div className="form-error" role="alert">
              {createResult.errordescription}
            </div>
          ) : null}
          {createResult && isOk(createResult) ? (
            <span className="chip ok" role="status">
              {t.cadastros.saved}
            </span>
          ) : null}
        </form>
      ) : null}
    </section>
  );
}
